import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { EmptyStateComponent } from '../../../components/empty-state/empty-state.component';
import { PaginationComponent } from '../../../components/pagination/pagination.component';
import { SkeletonComponent } from '../../../components/skeleton/skeleton.component';
import { SessionModel } from '../../../models/session.model';
import { DateToMomentPipe } from '../../../pipes/date-to-moment.pipe';
import { ConfirmService } from '../../../services/confirm.service';
import { SessionService } from '../../../services/session.service';
import { RouterLink } from '@angular/router';

/** Valor del filtro: cadena vacia = "todas". */
type ActiveFilter = '' | 'true' | 'false';

const PAGE_SIZE = 20;

/**
 * Listado global de sesiones de la app movil.
 *
 * Cada fila se puede expandir para ver la ficha completa de la sesion
 * (dispositivo, red y geolocalizacion) sin salir de la tabla.
 */
@Component({
  selector: 'app-sessions-page',
  imports: [
    RouterLink,
    DateToMomentPipe,
    PaginationComponent,
    EmptyStateComponent,
    SkeletonComponent,
    FormsModule,
    NgSelectComponent,
  ],
  templateUrl: './sessions-page.component.html',
  styleUrl: './sessions-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionsPageComponent implements OnInit {
  private readonly sessionService = inject(SessionService);
  private readonly confirmService = inject(ConfirmService);

  protected readonly sessions = this.sessionService.sessions;
  protected readonly loading = this.sessionService.loading;

  /**
   * `loaded` arranca en `false` y solo pasa a `true` cuando la peticion TERMINA
   * (con exito o con error). `loading` no basta: arranca en `false`, asi que el
   * primer render pintaria el estado vacio antes de saber si hay sesiones.
   */
  protected readonly loaded = this.sessionService.sessionsLoaded;
  protected readonly error = this.sessionService.sessionsError;
  protected readonly total = this.sessionService.total;
  protected readonly pageSize = this.sessionService.pageSize;

  protected readonly activeFilter = signal<ActiveFilter>('');
  protected readonly page = signal(1);

  /** Sesion cuya fila de detalle esta desplegada. */
  protected readonly expandedId = signal<string | null>(null);

  protected readonly hasFilters = computed(() => this.activeFilter() !== '');

  /**
   * Opciones fijas del filtro. "Todas las sesiones" es un item REAL de la
   * lista (no un placeholder): el ng-select se comporta identico al `<select>`
   * nativo que reemplaza.
   */
  protected readonly activeOptions: { value: ActiveFilter; label: string }[] = [
    { value: '', label: 'Todas las sesiones' },
    { value: 'true', label: 'Solo activas' },
    { value: 'false', label: 'Solo cerradas' },
  ];

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  protected async load(): Promise<void> {
    const active = this.activeFilter();
    await this.sessionService.getSessions({
      active: active === '' ? undefined : active === 'true',
      page: this.page(),
      pageSize: PAGE_SIZE,
    });
  }

  /**
   * `null` llega cuando se limpia el ng-select con el boton de "x"; se
   * resuelve exactamente igual que elegir "Todas las sesiones".
   */
  protected onActiveFilter(value: ActiveFilter | null): void {
    this.activeFilter.set(value ?? '');
    this.expandedId.set(null);
    this.page.set(1);
    void this.load();
  }

  protected clearFilters(): void {
    this.activeFilter.set('');
    this.expandedId.set(null);
    this.page.set(1);
    void this.load();
  }

  protected onPageChange(page: number): void {
    this.page.set(page);
    this.expandedId.set(null);
    void this.load();
  }

  protected toggle(session: SessionModel): void {
    this.expandedId.update((current) => (current === session.sessionId ? null : session.sessionId));
  }

  /** Coordenadas validas: ambas presentes. */
  protected hasCoordinates(session: SessionModel): boolean {
    return session.latitude !== null && session.longitude !== null;
  }

  protected mapsUrl(session: SessionModel): string {
    return `https://www.google.com/maps/search/?api=1&query=${session.latitude},${session.longitude}`;
  }

  /** Cerrar una sesion desconecta el dispositivo: se confirma en rojo. */
  protected async closeSession(session: SessionModel): Promise<void> {
    if (!session.active) return;

    const confirmed = await this.confirmService.ask({
      title: 'Cerrar sesión',
      message:
        'El dispositivo quedará desconectado de inmediato y tendrá que iniciar sesión de nuevo en la app móvil.',
      detail: `${session.deviceName || session.model} · ${session.location || session.ip}`,
      confirmLabel: 'Cerrar sesión',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) return;

    const closed = await this.sessionService.closeSession(session.sessionId);
    if (closed) await this.load();
  }
}
